import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatDuration } from "@/data/mockData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Headphones, FileText, Upload, Trash2, Bot, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ContentType, ContentStatus } from "@/types/content";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteContentItem, getContentListData, getProcessingStatus, saveContentItem, triggerContentProcessing, type UploadedAudioTrack } from "@/services/contentService";
import { getSettings } from "@/services/settingsService";
import { deleteFileByPublicUrl, uploadFileToBucket } from "@/services/storageService";
import { getCategories, getContentCategoryIds, saveContentCategories, type Category } from "@/services/categoryService";
import { PRICING_TIERS, tierByStoreProductId, tierByPrice } from "@/lib/pricing-tiers";

const AUDIO_MAX_BYTES = 150 * 1024 * 1024;
const DOC_MAX_BYTES = 50 * 1024 * 1024;
const COVER_MAX_BYTES = 10 * 1024 * 1024;

// Formatos compatibles con react-native-track-player en iOS + Android
// MP3: universal, máxima compatibilidad
// M4A/AAC: soportado en ambas plataformas (AVPlayer + ExoPlayer)
// OGG: NO soportado en iOS (AVPlayer no lo decodifica)
const AUDIO_ALLOWED_TYPES = new Set(["audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/aac"]);
const AUDIO_REJECTED_FORMATS: Record<string, string> = {
  "audio/ogg": "OGG no es compatible con iOS. Convierte a MP3 antes de subir.",
  "audio/wav": "WAV es demasiado pesado para streaming. Convierte a MP3.",
  "audio/flac": "FLAC no es compatible con iOS para streaming. Convierte a MP3.",
  "audio/webm": "WebM no es compatible con el reproductor móvil. Convierte a MP3.",
};
const AUDIO_RECOMMENDED_FORMAT = "MP3 (128-320 kbps)";
const DOC_ALLOWED_TYPES = new Set(["application/pdf", "text/plain"]);
const COVER_ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

type UploadState = {
  isUploading: boolean;
  error: string | null;
};

function isValidFile(file: File, allowedTypes: Set<string>, maxBytes: number, rejectedFormats?: Record<string, string>): string | null {
  // Check rejected formats first for specific error messages
  if (rejectedFormats && file.type in rejectedFormats) {
    return rejectedFormats[file.type];
  }
  if (!allowedTypes.has(file.type)) {
    const ext = file.name.split(".").pop()?.toLowerCase() || "desconocido";
    return `Formato .${ext} (${file.type || "desconocido"}) no compatible. Usa ${AUDIO_RECOMMENDED_FORMAT}.`;
  }
  if (file.size > maxBytes) {
    return `Archivo excede el límite de ${(maxBytes / (1024 * 1024)).toFixed(0)}MB.`;
  }
  return null;
}

function getUploadErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  const message = (error.message || "").toLowerCase();
  if (message.includes("bucket") && message.includes("not found")) {
    return "No existe el bucket de Storage. Crea 'content-docs' en Supabase Storage.";
  }
  if (message.includes("row-level security") || message.includes("permission denied")) {
    return "Storage bloqueó la subida por permisos. Revisa políticas del bucket.";
  }
  return error.message || fallback;
}

function inferContentType(audioUrl: string, docUrl: string, fallback: ContentType = "audio"): ContentType {
  const hasAudio = Boolean(audioUrl?.trim());
  const hasDoc = Boolean(docUrl?.trim());
  if (hasAudio && hasDoc) return "mixed";
  if (hasAudio) return "audio";
  if (hasDoc) return "pdf";
  return fallback;
}

async function getAudioDurationInSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    const audio = document.createElement("audio");
    const objectUrl = URL.createObjectURL(file);
    audio.preload = "metadata";
    audio.src = objectUrl;
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? Math.round(audio.duration) : 0;
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(0);
    };
  });
}

export default function ContentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isNew = id === "new" || !id;
  const { data, isLoading } = useQuery({
    queryKey: ["content-list-data"],
    queryFn: getContentListData,
  });
  const { data: allCategories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  const existing = useMemo(
    () => (isNew ? null : (data?.contentItems || []).find((c) => c.id === id) || null),
    [data, id, isNew],
  );
  const audio = useMemo(
    () => (existing ? (data?.audioAssets || []).find((a) => a.content_item_id === existing.id) || null : null),
    [data, existing],
  );
  const pdf = useMemo(
    () => (existing ? (data?.pdfAssets || []).find((p) => p.content_item_id === existing.id) || null : null),
    [data, existing],
  );
  const accessRule = useMemo(
    () => (existing ? (data?.accessRules || []).find((rule) => rule.content_item_id === existing.id) || null : null),
    [data, existing],
  );
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [author, setAuthor] = useState("");
  const [contentType, setContentType] = useState<ContentType>("audio");
  const [status, setStatus] = useState<ContentStatus>("draft");
  const [category, setCategory] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [descShort, setDescShort] = useState("");
  const [descLong, setDescLong] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [tags, setTags] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [coverMimeType, setCoverMimeType] = useState("");
  const [audioMode, setAudioMode] = useState<"single_file" | "chapters">("single_file");
  const [audioUrl, setAudioUrl] = useState("");
  const [audioFileSizeMb, setAudioFileSizeMb] = useState(0);
  const [audioMimeType, setAudioMimeType] = useState("");
  const [audioTracks, setAudioTracks] = useState<UploadedAudioTrack[]>([]);
  const [narrator, setNarrator] = useState("");
  const [audioQuality, setAudioQuality] = useState("320kbps");
  const [totalDuration, setTotalDuration] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [pdfFileSizeMb, setPdfFileSizeMb] = useState(0);
  const [pdfMimeType, setPdfMimeType] = useState("");
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [previewPageCount, setPreviewPageCount] = useState(10);
  const [pdfIsDownloadable, setPdfIsDownloadable] = useState(true);
  const [accessType, setAccessType] = useState<"free" | "subscription" | "single_purchase">("free");
  const [accessPrice, setAccessPrice] = useState("");
  const [accessStoreProductId, setAccessStoreProductId] = useState("");
  const [isDocDropActive, setIsDocDropActive] = useState(false);
  const [coverUpload, setCoverUpload] = useState<UploadState>({ isUploading: false, error: null });
  const [audioUpload, setAudioUpload] = useState<UploadState>({ isUploading: false, error: null });
  const [docUpload, setDocUpload] = useState<UploadState>({ isUploading: false, error: null });
  const docInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!existing) return;
    setTitle(existing.title || "");
    setSubtitle(existing.subtitle || "");
    setAuthor(existing.author || "");
    setContentType(existing.content_type || "audio");
    setStatus(existing.status || "draft");
    setCategory(existing.category || "");
    // Load categories from bridge table
    getContentCategoryIds(existing.id).then(setSelectedCategoryIds).catch(() => {});
    setDescShort(existing.description_short || "");
    setDescLong(existing.description_long || "");
    setIsFeatured(existing.is_featured || false);
    setTags((existing.tags || []).join(", "));
    setCoverImageUrl(existing.cover_image_url || "");
    setAudioMode(audio?.audio_mode || "single_file");
    setAudioUrl(audio?.audio_url || "");
    setAudioFileSizeMb(audio?.file_size_mb || 0);
    setAudioMimeType(audio?.mime_type || "");
    setAudioTracks(
      audio?.audio_url
        ? [
            {
              url: audio.audio_url,
              title: "Audio principal",
              durationSeconds: audio.total_duration_seconds || 0,
              sizeMb: audio.file_size_mb || 0,
              mimeType: audio.mime_type || "",
            },
          ]
        : [],
    );
    setNarrator(audio?.narrator || "");
    setAudioQuality(audio?.audio_quality || "320kbps");
    setTotalDuration(audio?.total_duration_seconds || 0);
    setPdfUrl(pdf?.pdf_url || "");
    setPreviewPdfUrl(pdf?.preview_pdf_url || "");
    setPdfFileSizeMb(pdf?.file_size_mb || 0);
    setPdfMimeType(pdf?.mime_type || "");
    setPdfPageCount(pdf?.page_count || 0);
    setPreviewPageCount(pdf?.preview_page_count || 10);
    setPdfIsDownloadable(pdf?.is_downloadable ?? true);
    setAccessType(accessRule?.access_type || "free");
    setAccessPrice(accessRule?.price != null ? String(accessRule.price) : "");
    // Resolve store_product_id: prefer the explicit column, else infer from price for legacy rows.
    const tier =
      tierByStoreProductId((accessRule as any)?.store_product_id) ||
      tierByPrice(accessRule?.price != null ? Number(accessRule.price) : null);
    setAccessStoreProductId(tier?.storeProductId || "");
  }, [existing, audio, pdf, accessRule]);

  const onCoverUpload = async (file: File) => {
    const validationError = isValidFile(file, COVER_ALLOWED_TYPES, COVER_MAX_BYTES);
    if (validationError) {
      setCoverUpload({ isUploading: false, error: validationError });
      return;
    }

    try {
      setCoverUpload({ isUploading: true, error: null });
      const uploaded = await uploadFileToBucket({ bucket: "content-covers", file, folder: "covers" });
      setCoverImageUrl(uploaded.publicUrl);
      setCoverMimeType(uploaded.mimeType);
      setCoverUpload({ isUploading: false, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : "No se pudo subir la portada.";
      setCoverUpload({ isUploading: false, error: message });
    }
  };

  const onAudioUpload = async (files: FileList | File[]) => {
    const inputFiles = Array.from(files);
    if (inputFiles.length === 0) return;
    for (const file of inputFiles) {
      const validationError = isValidFile(file, AUDIO_ALLOWED_TYPES, AUDIO_MAX_BYTES, AUDIO_REJECTED_FORMATS);
      if (validationError) {
        setAudioUpload({ isUploading: false, error: validationError });
        return;
      }
    }

    try {
      setAudioUpload({ isUploading: true, error: null });

      const uploadedTracks: UploadedAudioTrack[] = [];
      for (const file of inputFiles) {
        const [uploaded, duration] = await Promise.all([
          uploadFileToBucket({ bucket: "content-audio", file, folder: "audio" }),
          getAudioDurationInSeconds(file),
        ]);
        uploadedTracks.push({
          url: uploaded.publicUrl,
          title: file.name.replace(/\.[^.]+$/, ""),
          durationSeconds: duration,
          sizeMb: uploaded.sizeMb,
          mimeType: uploaded.mimeType,
        });
      }

      const nextTracks = [...audioTracks, ...uploadedTracks];
      const durationTotal = nextTracks.reduce((sum, track) => sum + (track.durationSeconds || 0), 0);
      const sizeTotal = Number(nextTracks.reduce((sum, track) => sum + (track.sizeMb || 0), 0).toFixed(2));
      const firstTrack = nextTracks[0];

      // Auto-detect bitrate from file size and duration
      if (durationTotal > 0 && sizeTotal > 0) {
        const bitrate = Math.round((sizeTotal * 8 * 1024) / durationTotal);
        const label = bitrate >= 256 ? "320kbps" : bitrate >= 160 ? "256kbps" : "128kbps";
        setAudioQuality(label);
      }

      setAudioTracks(nextTracks);
      setAudioUrl(firstTrack?.url || "");
      setAudioFileSizeMb(sizeTotal);
      setAudioMimeType(firstTrack?.mimeType || "");
      setTotalDuration(durationTotal);
      setAudioMode(nextTracks.length > 1 ? "chapters" : "single_file");
      setContentType(inferContentType(firstTrack?.url || "", pdfUrl, contentType));
      setAudioUpload({ isUploading: false, error: null });
    } catch (error) {
      const message = getUploadErrorMessage(error, "No se pudo subir el audio.");
      setAudioUpload({ isUploading: false, error: message });
    }
  };

  const onDocUpload = async (file: File) => {
    const validationError = isValidFile(file, DOC_ALLOWED_TYPES, DOC_MAX_BYTES);
    if (validationError) {
      setDocUpload({ isUploading: false, error: validationError });
      return;
    }

    try {
      setDocUpload({ isUploading: true, error: null });
      const uploaded = await uploadFileToBucket({ bucket: "content-docs", file, folder: "docs" });
      setPdfUrl(uploaded.publicUrl);
      setPdfFileSizeMb(uploaded.sizeMb);
      setPdfMimeType(uploaded.mimeType);
      setContentType(inferContentType(audioUrl, uploaded.publicUrl, contentType));
      setDocUpload({ isUploading: false, error: null });
    } catch (error) {
      const message = getUploadErrorMessage(error, "No se pudo subir el documento.");
      setDocUpload({ isUploading: false, error: message });
    }
  };

  const onDeleteStoredFile = async (kind: "cover" | "audio" | "doc") => {
    try {
      if (kind === "cover" && coverImageUrl) {
        await deleteFileByPublicUrl({ bucket: "content-covers", publicUrl: coverImageUrl });
        setCoverImageUrl("");
        setCoverMimeType("");
      }

      if (kind === "audio" && audioUrl) {
        for (const track of audioTracks) {
          await deleteFileByPublicUrl({ bucket: "content-audio", publicUrl: track.url });
        }
        if (audioTracks.length === 0) {
          await deleteFileByPublicUrl({ bucket: "content-audio", publicUrl: audioUrl });
        }
        setAudioUrl("");
        setAudioMimeType("");
        setAudioFileSizeMb(0);
        setTotalDuration(0);
        setAudioTracks([]);
        setAudioMode("single_file");
        setContentType(inferContentType("", pdfUrl, "audio"));
      }

      if (kind === "doc" && pdfUrl) {
        await deleteFileByPublicUrl({ bucket: "content-docs", publicUrl: pdfUrl });
        setPdfUrl("");
        setPdfMimeType("");
        setPdfFileSizeMb(0);
        setPdfPageCount(0);
        setContentType(inferContentType(audioUrl, "", "audio"));
      }

      toast({ title: "Archivo eliminado", description: "Se eliminó el archivo de Storage." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "No se pudo eliminar el archivo.";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const saveMutation = useMutation({
    mutationFn: saveContentItem,
    onSuccess: async (result) => {
      await saveContentCategories(result.id, selectedCategoryIds);
      await queryClient.invalidateQueries({ queryKey: ["content-list-data"] });
      await queryClient.invalidateQueries({ queryKey: ["content-categories-map"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
      toast({
        title: "Guardado",
        description: `"${title}" ${isNew ? "creado" : "actualizado"} en ${result.source === "supabase" ? "Supabase" : "mock"}.`,
      });
      navigate(`/admin/content/${result.id}`);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "No se pudo guardar el contenido.";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!existing?.id) throw new Error("No hay contenido para eliminar.");
      return deleteContentItem(existing.id);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["content-list-data"] });
      await queryClient.invalidateQueries({ queryKey: ["content-categories-map"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
      toast({
        title: "Contenido eliminado",
        description:
          result.warnings.length > 0
            ? `Se eliminó en DB, con ${result.warnings.length} advertencia(s) de limpieza de archivos.`
            : "Se eliminó correctamente.",
      });
      navigate("/admin/content");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "No se pudo eliminar el contenido.";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const handleDeleteContent = () => {
    if (!existing?.id) return;
    const ok = window.confirm(`¿Eliminar "${existing.title}"? Esta acción no se puede deshacer.`);
    if (!ok) return;
    deleteMutation.mutate();
  };

  /** Returns error message if content can't be published, or null if OK */
  const canPublish = (): string | null => {
    const hasAudio = Boolean(audioUrl || (audioTracks && audioTracks.length > 0));
    const hasPdf = Boolean(pdfUrl);
    if (!hasAudio && !hasPdf) return "Sube al menos un archivo (audio o PDF) antes de publicar.";
    // Use inferred type based on actual files
    const inferred = inferContentType(audioUrl, pdfUrl, contentType);
    const needsAudio = inferred === "audio" || inferred === "mixed";
    const needsPdf = inferred === "pdf" || inferred === "mixed";
    const missing: string[] = [];
    if (needsAudio && !hasAudio) missing.push("audio");
    if (needsPdf && !hasPdf) missing.push("PDF");
    if (missing.length > 0) return `Falta subir: ${missing.join(" y ")}.`;
    // Single-purchase requiere tier asignado (sin tier la app no puede mostrar
    // el botón de compra individual en el paywall).
    if (accessType === "single_purchase" && !accessStoreProductId) {
      return "Tipo de acceso es Compra individual pero no asignaste un tier de precio. Ve a la pestaña Acceso y selecciona uno.";
    }
    return null;
  };

  // Warning visual: el contenido es single_purchase sin tier asignado.
  // Aplica sin importar si está publicado o no, para alertar temprano.
  const missingTierWarning =
    accessType === "single_purchase" && !accessStoreProductId;

  const handleSave = () => {
    if (!title.trim()) {
      toast({ title: "Error", description: "El título es obligatorio.", variant: "destructive" });
      return;
    }
    if (status === "published") {
      const publishError = canPublish();
      if (publishError) {
        toast({ title: "No se puede publicar", description: publishError, variant: "destructive" });
        return;
      }
    }
    const resolvedContentType = inferContentType(audioUrl, pdfUrl, contentType);
    setContentType(resolvedContentType);
    saveMutation.mutate({
      id: existing?.id,
      slug: existing?.slug,
      title,
      subtitle,
      author,
      category,
      contentType: resolvedContentType,
      status,
      isFeatured,
      descriptionShort: descShort,
      descriptionLong: descLong,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      coverImageUrl,
      audioMode,
      audioUrl,
      audioFileSizeMb,
      audioMimeType,
      audioTracks,
      narrator,
      audioQuality,
      totalDurationSeconds: totalDuration || audio?.total_duration_seconds || 0,
      pdfUrl,
      previewPdfUrl,
      pdfFileSizeMb,
      pdfMimeType,
      pdfPageCount,
      previewPageCount,
      pdfIsDownloadable,
      accessType,
      price: accessType === "single_purchase" ? Number(accessPrice) || 0 : null,
      storeProductId: accessType === "single_purchase" ? accessStoreProductId || null : null,
    });
  };

  if (!isNew && isLoading) {
    return <div className="py-10 text-sm text-muted-foreground">Cargando contenido...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/content">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-foreground">
            {isNew ? "Nuevo contenido" : existing?.title || "Editar contenido"}
          </h1>
          {existing && (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">{existing.content_type}</Badge>
              <span className="text-xs text-muted-foreground">ID: {existing.id}</span>
            </div>
          )}
        </div>
        <Button variant="default" onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending ? "Guardando..." : "Guardar"}
        </Button>
        {!isNew && (
          <Button
            variant="outline"
            onClick={handleDeleteContent}
            disabled={deleteMutation.isPending || saveMutation.isPending}
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
          </Button>
        )}
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="audio">Audio</TabsTrigger>
          <TabsTrigger value="pdf">Lectura</TabsTrigger>
          <TabsTrigger value="access" className="gap-1.5">
            Acceso
            {missingTierWarning && <span className="text-amber-600" title="Falta tier de precio">⚠</span>}
          </TabsTrigger>
          <TabsTrigger value="publishing">Publicación</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Información general</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título del contenido" />
                </div>
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Subtítulo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Autor</Label>
                  <Input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Nombre del autor" />
                </div>
                <div className="space-y-2">
                  <Label>Categorías</Label>
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-input rounded-md bg-background">
                    {selectedCategoryIds.map((cid) => {
                      const cat = allCategories.find((c) => c.id === cid);
                      if (!cat) return null;
                      return (
                        <Badge key={cid} variant="secondary" className="gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setSelectedCategoryIds((prev) => prev.filter((id) => id !== cid))}>
                          {cat.name} ×
                        </Badge>
                      );
                    })}
                    <select
                      className="bg-transparent border-none text-sm text-muted-foreground outline-none min-w-[140px] flex-1"
                      value=""
                      onChange={(e) => {
                        if (e.target.value && !selectedCategoryIds.includes(e.target.value)) {
                          setSelectedCategoryIds((prev) => [...prev, e.target.value]);
                        }
                      }}
                    >
                      <option value="">+ Agregar categoría</option>
                      {allCategories
                        .filter((c) => !selectedCategoryIds.includes(c.id))
                        .map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground">Selecciona una o más. Gestiona categorías desde el tab "Categorías" en Contenido.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de contenido (automático)</Label>
                  <Input
                    value={contentType === "mixed" ? "Mixto" : contentType === "pdf" ? "Lectura" : "Audio"}
                    disabled
                  />
                  <p className="text-xs text-muted-foreground">
                    Se calcula según archivos cargados: audio + documento = mixto.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Tags (separados por coma)</Label>
                  <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="fe, clásico, oración" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descripción corta</Label>
                <Textarea value={descShort} onChange={e => setDescShort(e.target.value)} placeholder="Resumen breve" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Descripción larga</Label>
                <Textarea value={descLong} onChange={e => setDescLong(e.target.value)} placeholder="Descripción completa" rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Portada (JPG, PNG, WebP - máximo 10MB)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onCoverUpload(file);
                      e.currentTarget.value = "";
                    }}
                    disabled={coverUpload.isUploading}
                  />
                  {coverImageUrl && (
                    <Button type="button" variant="outline" onClick={() => onDeleteStoredFile("cover")}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Quitar
                    </Button>
                  )}
                </div>
                {coverUpload.isUploading && <p className="text-xs text-muted-foreground">Subiendo portada...</p>}
                {coverUpload.error && <p className="text-xs text-destructive">{coverUpload.error}</p>}
                {coverImageUrl && (
                  <div className="rounded-md border border-border p-2">
                    <img src={coverImageUrl} alt="Portada" className="h-24 w-16 object-cover rounded" />
                    <p className="text-xs text-muted-foreground mt-2">URL: {coverImageUrl}</p>
                    {coverMimeType && <p className="text-xs text-muted-foreground">MIME: {coverMimeType}</p>}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audio" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Configuración de audio</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Modo de audio (automático)</Label>
                      <Input value={audioMode === "chapters" ? "Por capítulos" : "Archivo único"} disabled />
                      <p className="text-xs text-muted-foreground">
                        Se detecta automáticamente: 1 archivo = único, 2+ archivos = capítulos.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Narrador</Label>
                      <Input value={narrator} onChange={(e) => setNarrator(e.target.value)} placeholder="Nombre del narrador" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Calidad de audio</Label>
                      <Input value={audioQuality || "Se detecta al subir archivo"} disabled />
                      <p className="text-xs text-muted-foreground">
                        Se detecta automáticamente del archivo subido.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Duración total</Label>
                      <Input value={formatDuration(totalDuration || audio?.total_duration_seconds || 0)} disabled placeholder="Calculado automáticamente" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Archivos de audio (MP3, M4A - máximo 150MB por archivo)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        multiple
                        accept=".mp3,.m4a,.aac,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac"
                        onChange={(e) => {
                          if (e.target.files?.length) onAudioUpload(e.target.files);
                          e.currentTarget.value = "";
                        }}
                        disabled={audioUpload.isUploading}
                      />
                      {audioUrl && (
                        <Button type="button" variant="outline" onClick={() => onDeleteStoredFile("audio")}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Quitar
                        </Button>
                      )}
                    </div>
                    {audioUpload.isUploading && <p className="text-xs text-muted-foreground">Subiendo audio...</p>}
                    {audioUpload.error && <p className="text-xs text-destructive">{audioUpload.error}</p>}
                    {audioTracks.length > 0 && (
                      <div className="rounded-md border border-border p-2 text-xs text-muted-foreground space-y-1">
                        <p>URL: {audioUrl}</p>
                        <p>Tamaño: {audioFileSizeMb} MB</p>
                        <p>MIME: {audioMimeType || "no detectado"}</p>
                      </div>
                    )}
                  </div>

                  {audioTracks.length > 1 && (
                    <div className="mt-4">
                      <Label className="mb-2 block">Capítulos detectados ({audioTracks.length})</Label>
                      <div className="rounded-lg border border-border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Título</TableHead>
                              <TableHead>Duración</TableHead>
                              <TableHead>Publicado</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {audioTracks.map((track, index) => (
                              <TableRow key={track.url}>
                                <TableCell className="font-medium">{index + 1}</TableCell>
                                <TableCell>{track.title || `Capítulo ${index + 1}`}</TableCell>
                                <TableCell className="text-muted-foreground">{formatDuration(track.durationSeconds || 0)}</TableCell>
                                <TableCell><Badge className="text-xs">Sí</Badge></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
              </>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pdf" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Configuración de lectura</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <>
                  <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>URL del documento</Label>
                      <Input
                        value={pdfUrl}
                        onChange={(e) => {
                          const next = e.target.value;
                          setPdfUrl(next);
                          setContentType(inferContentType(audioUrl, next, contentType));
                        }}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño del archivo (MB)</Label>
                      <Input type="number" value={pdfFileSizeMb} onChange={(e) => setPdfFileSizeMb(Number(e.target.value) || 0)} placeholder="Ej: 12.5" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Número de páginas</Label>
                      <Input type="number" value={pdfPageCount} onChange={(e) => setPdfPageCount(Number(e.target.value) || 0)} placeholder="Ej: 350" />
                    </div>
                    <div className="space-y-2">
                      <Label>Páginas de preview</Label>
                      <Input type="number" value={previewPageCount} onChange={(e) => setPreviewPageCount(Math.max(1, Number(e.target.value) || 10))} placeholder="10" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>URL preview</Label>
                      <Input value={previewPdfUrl} onChange={(e) => setPreviewPdfUrl(e.target.value)} placeholder="https://..." />
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <Switch checked={pdfIsDownloadable} onCheckedChange={setPdfIsDownloadable} />
                      <Label>Permitir descarga</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Documento de lectura (PDF o TXT - máximo 50MB)</Label>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => docInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          docInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDocDropActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDocDropActive(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDocDropActive(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) onDocUpload(file);
                      }}
                      className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                        isDocDropActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium text-foreground">
                        Arrastra tu documento aquí o haz clic para subir
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Formatos permitidos: .pdf, .txt</p>
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".pdf,.txt,application/pdf,text/plain"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onDocUpload(file);
                          e.currentTarget.value = "";
                        }}
                        disabled={docUpload.isUploading}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {pdfUrl && (
                        <Button type="button" variant="outline" onClick={() => onDeleteStoredFile("doc")}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Quitar
                        </Button>
                      )}
                    </div>
                    {docUpload.isUploading && <p className="text-xs text-muted-foreground">Subiendo documento...</p>}
                    {docUpload.error && <p className="text-xs text-destructive">{docUpload.error}</p>}
                    {pdfUrl && (
                      <div className="rounded-md border border-border p-2 text-xs text-muted-foreground space-y-1">
                        <p>URL: {pdfUrl}</p>
                        <p>Preview: {previewPdfUrl || "pendiente / no configurado"}</p>
                        <p>Tamaño: {pdfFileSizeMb} MB</p>
                        <p>MIME: {pdfMimeType || "no detectado"}</p>
                        <p>Páginas preview: {previewPageCount}</p>
                      </div>
                    )}
                  </div>
              </>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="access" className="space-y-4 mt-4">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6 space-y-2 text-xs">
              <p className="font-heading font-bold text-foreground text-sm">
                Cómo funciona el acceso en Senda
              </p>
              <ul className="space-y-1.5 text-muted-foreground leading-relaxed">
                <li>
                  <span className="font-semibold text-foreground">Suscripción premium activa</span> →
                  acceso a <span className="font-semibold">todos los títulos</span>, incluso los marcados como compra individual.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Sin suscripción</span> → puede ver
                  títulos <code>Gratuitos</code>, comprar
                  individualmente los <code>Compra individual</code> (acceso permanente a ese título), o suscribirse para acceso total.
                </li>
                <li>
                  Una compra individual se conserva <span className="font-semibold">para siempre</span>, aunque cancele la suscripción (salvo refund).
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-heading">Reglas de acceso</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de acceso</Label>
                <Select
                  value={accessType}
                  onValueChange={(value) => {
                    const next = value as "free" | "subscription" | "single_purchase";
                    setAccessType(next);
                    if (next !== "single_purchase") {
                      setAccessPrice("");
                      setAccessStoreProductId("");
                    }
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Gratuito — accesible para todos</SelectItem>
                    <SelectItem value="subscription">Suscripción — requiere suscripción activa</SelectItem>
                    <SelectItem value="single_purchase">Compra individual — precio fijo por tier</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {accessType === "free" && "Cualquier usuario (incluido guest si está habilitada la navegación sin cuenta) puede leer/escuchar este título sin pagar."}
                  {accessType === "subscription" && "Solo accesible para usuarios con suscripción premium activa. Si cancelan, mantienen acceso durante el grace period configurado en Ajustes → Audio (default 7 días) y luego se bloquea."}
                  {accessType === "single_purchase" && "El usuario paga el precio del tier y obtiene acceso permanente a este título. Los suscriptores premium también lo ven desbloqueado automáticamente sin tener que comprarlo."}
                </p>
              </div>

              {missingTierWarning && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs">
                  <span className="text-base leading-none">⚠️</span>
                  <div className="space-y-0.5">
                    <p className="font-bold text-amber-900">
                      Falta asignar un tier de precio
                    </p>
                    <p className="text-amber-800 leading-relaxed">
                      Sin tier, el paywall en la app NO mostrará el botón de
                      compra individual y los usuarios sin suscripción no
                      podrán comprar este título. Selecciona un tier abajo
                      antes de publicar.
                    </p>
                  </div>
                </div>
              )}

              {accessType === "single_purchase" && (
                <div className="space-y-2 pt-2 border-t">
                  <Label>Tier de precio</Label>
                  <Select
                    value={accessStoreProductId || ""}
                    onValueChange={(val) => {
                      setAccessStoreProductId(val);
                      const t = PRICING_TIERS.find((x) => x.storeProductId === val);
                      setAccessPrice(t ? String(t.price) : "");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un tier…" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICING_TIERS.map((t) => (
                        <SelectItem key={t.id} value={t.storeProductId}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {accessStoreProductId && (
                    <div className="text-xs bg-muted/50 border rounded-md px-3 py-2 space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Precio:</span>
                        <span className="font-semibold text-foreground">${Number(accessPrice).toFixed(2)} USD</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Store Product ID:</span>
                        <span className="font-mono text-[11px] text-foreground">{accessStoreProductId}</span>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Este ID debe existir como producto en App Store Connect bajo el bundle <code className="text-[10px]">com.sendadevida.app</code> y estar enlazado en RevenueCat. Los tiers listados aquí son los confirmados con Eduardo (16 Abr 2026).
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="publishing" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Estado de publicación</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={status} onValueChange={v => {
                    if (v === "published") {
                      const publishError = canPublish();
                      if (publishError) {
                        toast({ title: "No se puede publicar", description: publishError, variant: "destructive" });
                        return;
                      }
                    }
                    setStatus(v as ContentStatus);
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Borrador</SelectItem>
                      <SelectItem value="published">Publicado</SelectItem>
                      <SelectItem value="archived">Archivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={isFeatured} onCheckedChange={setIsFeatured} />
                  <Label>Contenido destacado</Label>
                </div>
              </div>
              {existing && (
                <div className="text-xs text-muted-foreground space-y-1 pt-2">
                  <p>Creado: {new Date(existing.created_at).toLocaleDateString("es-ES")}</p>
                  <p>Actualizado: {new Date(existing.updated_at).toLocaleDateString("es-ES")}</p>
                  {existing.published_at && <p>Publicado: {new Date(existing.published_at).toLocaleDateString("es-ES")}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {!isNew && id && (
        <IAProcessingCard contentItemId={id} />
      )}
    </div>
  );
}

function IAProcessingCard({ contentItemId }: { contentItemId: string }) {
  const { toast } = useToast();

  const { data: ragSettings } = useQuery({
    queryKey: ["rag-settings"],
    queryFn: () => getSettings(["rag_enabled", "rag_openai_api_key"]),
    staleTime: 60_000,
  });

  const ragEnabled = ragSettings?.rag_enabled === "true";
  const hasApiKey = !!ragSettings?.rag_openai_api_key;

  const { data: ps, isLoading } = useQuery({
    queryKey: ["processing-status", contentItemId],
    queryFn: () => getProcessingStatus(contentItemId),
    refetchInterval: (query) =>
      query.state.data?.status === "processing" ? 3000 : false,
  });

  const { mutate: trigger, isPending } = useMutation({
    mutationFn: () => triggerContentProcessing(contentItemId),
    onSuccess: (result) => {
      toast({ title: "Procesamiento completado", description: `${result.chunks_count} fragmentos generados.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error al procesar", description: err.message, variant: "destructive" });
    },
  });

  const isProcessing = ps?.status === "processing" || isPending;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bot className="h-4 w-4" />
          Asistente IA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            {isLoading ? (
              <span className="text-sm text-muted-foreground">Verificando estado...</span>
            ) : !ps ? (
              <span className="text-sm text-muted-foreground">No procesado — el asistente no tiene acceso a este contenido.</span>
            ) : ps.status === "completed" ? (
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5 text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium">Listo para IA</span>
                  <span className="text-green-600 font-mono">· {ps.chunks_count} fragmentos</span>
                </div>
                {ps.completed_at && (
                  <p className="text-xs text-muted-foreground">
                    Procesado el {new Date(ps.completed_at).toLocaleString("es-ES")}
                  </p>
                )}
              </div>
            ) : ps.status === "processing" ? (
              <div className="flex items-center gap-1.5 text-sm text-yellow-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Procesando...</span>
              </div>
            ) : ps.status === "failed" ? (
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Error al procesar</span>
                </div>
                {ps.error_message && (
                  <p className="text-xs text-red-600 font-mono">{ps.error_message}</p>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Pendiente de procesamiento</span>
            )}
          </div>
          <Button
            variant={ps?.status === "completed" ? "outline" : "default"}
            size="sm"
            onClick={() => trigger()}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Procesando...</>
            ) : ps?.status === "completed" ? (
              <><RefreshCw className="h-4 w-4 mr-2" />Reprocesar</>
            ) : (
              <><Bot className="h-4 w-4 mr-2" />Procesar para IA</>
            )}
          </Button>
        </div>
        {ragSettings && !hasApiKey && (
          <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
            <p className="text-xs text-yellow-700">
              Falta la API Key de OpenAI. El procesamiento fallará.{" "}
              <a href="/admin/settings?tab=ia" className="underline font-medium">Configurar en Ajustes → IA / Chat</a>.
            </p>
          </div>
        )}
        {ragSettings && !ragEnabled && (
          <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/40 px-3 py-2">
            <Bot className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              El chat con IA está desactivado en la app. Puedes procesar igualmente para preparar el contenido antes de activarlo.
            </p>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          El procesamiento extrae el texto del contenido, lo divide en fragmentos y genera embeddings para el asistente de chat.
        </p>
      </CardContent>
    </Card>
  );
}
